// Types
import type { Action } from "./types";

/**
 * Super admin Revenue screen: the fee revenue Aya collected each month, each
 * day or between two dates, the invoices behind them, and the invoices not
 * settled yet. Aya's own entities and test businesses are never listed.
 */

export type RevenueFlagStatus = "Collected" | "ToCollect";

/**
 * One row per month from November 2026.
 *
 * @return {*} {Action}
 */
export const fetchRevenueMonths = (): Action => ({
  type: "FETCH_REVENUE_MONTHS",
});

/**
 * One row per day of a month.
 *
 * @param {string} month YYYY-MM
 * @return {*} {Action}
 */
export const fetchRevenueDays = (month: string): Action => ({
  type: "FETCH_REVENUE_DAYS",
  data: month,
});

/**
 * One row per day from `from` to `to`, Toronto days, inclusive.
 *
 * @param {*} data YYYY-MM-DD days
 * @return {*} {Action}
 */
export const fetchRevenueRange = (data: { from: string; to: string }): Action => ({
  type: "FETCH_REVENUE_RANGE",
  data,
});

/**
 * The invoices settled from `from` to `to`, Toronto days, inclusive.
 *
 * @param {*} data
 * @return {*} {Action}
 */
export const fetchRevenueInvoices = (data: { from: string; to: string }): Action => ({
  type: "FETCH_REVENUE_INVOICES",
  data,
});

/**
 * What came into and went out of Aya Master's Return bucket each month from
 * November 2026, and every move out of it.
 *
 * @return {*} {Action}
 */
export const fetchRevenueMaster = (): Action => ({
  type: "FETCH_REVENUE_MASTER",
});

export type RevenueSweepKind = "Revenue" | "Tax";

export type RevenueSweepAction = "send" | "retry" | "resolve" | "cancel";

/** What a sweep covers: the kind, the tax types for a tax sweep, the days. */
export type RevenueSweepSource = "AyaMaster" | "BusinessTaxBucket" | "PrepaySpendTax";

export interface RevenueSweepScope {
  // AyaMaster: revenue or the tax on fees. BusinessTaxBucket: the tax on
  // contributions and claims in the businesses' tax buckets. PrepaySpendTax: the
  // tax owed on what prepay members spent, paid out of those buckets.
  source: RevenueSweepSource;
  kind: RevenueSweepKind;
  components: string[];
  // Empty for every province.
  provinces: string[];
  // Tax on fees only: AdminFee, PepmFee, OtherFees; empty for every fee.
  feeGroups?: string[];
  from: string;
  to: string;
  leftOut: string[];
}

/**
 * The sweeps prepared so far, newest first.
 *
 * @return {*} {Action}
 */
export const fetchRevenueSweeps = (): Action => ({
  type: "FETCH_REVENUE_SWEEPS",
});

/**
 * The prepay tax payouts, newest first.
 *
 * @return {*} {Action}
 */
export const fetchPrepayTaxPayouts = (): Action => ({
  type: "FETCH_PREPAY_TAX_PAYOUTS",
});

/**
 * The EFT funding account a sweep leaves through and the DC Bank accounts it
 * can go to.
 *
 * @return {*} {Action}
 */
export const fetchRevenueSweepAccounts = (): Action => ({
  type: "FETCH_REVENUE_SWEEP_ACCOUNTS",
});

/**
 * What a sweep of these days would move.
 *
 * @param {RevenueSweepScope} data
 * @return {*} {Action}
 */
export const previewRevenueSweep = (data: RevenueSweepScope): Action => ({
  type: "PREVIEW_REVENUE_SWEEP",
  data,
});

/**
 * Claims the invoices of the preview for a new sweep. Nothing moves yet.
 *
 * @param {*} data
 * @return {*} {Action}
 */
export const prepareRevenueSweep = (
  data: RevenueSweepScope & { destinationAccountId: number; note: string }
): Action => ({
  type: "PREPARE_REVENUE_SWEEP",
  data,
});

/**
 * @param {string} guid
 * @return {*} {Action}
 */
export const fetchRevenueSweep = (guid: string): Action => ({
  type: "FETCH_REVENUE_SWEEP",
  data: guid,
});

/**
 * One step on a sweep: send the money, retry one left to check, resolve one
 * by hand after checking DC Bank, or cancel one not sent.
 *
 * @param {*} data
 * @return {*} {Action}
 */
export const runRevenueSweepAction = (data: {
  guid: string;
  action: RevenueSweepAction;
  moved?: boolean;
  note?: string;
}): Action => ({
  type: "RUN_REVENUE_SWEEP_ACTION",
  data,
});

/**
 * The invoices not settled yet: pending, sent, in transit or failed.
 *
 * @return {*} {Action}
 */
export const fetchRevenuePending = (): Action => ({
  type: "FETCH_REVENUE_PENDING",
});

/**
 * @param {string} invoiceNumber
 * @return {*} {Action}
 */
export const fetchRevenueInvoice = (invoiceNumber: string): Action => ({
  type: "FETCH_REVENUE_INVOICE",
  data: invoiceNumber,
});

/**
 * Records the super admin's collection flag on an invoice. `refresh` names
 * the list to read again so its totals follow the flag.
 *
 * @param {*} data
 * @return {*} {Action}
 */
export const flagRevenueInvoice = (data: {
  invoiceNumber: string;
  status: RevenueFlagStatus;
  note: string;
  refresh: Action | null;
}): Action => ({
  type: "FLAG_REVENUE_INVOICE",
  data,
});

/**
 * @param {*} data partial revenue state
 * @return {*} {Action}
 */
export const setRevenue = (data: any): Action => ({
  type: "SET_REVENUE",
  data,
});

/**
 * Clears the success or error message shown after a flag.
 *
 * @return {*} {Action}
 */
export const clearRevenueMessage = (): Action => ({
  type: "CLEAR_REVENUE_MESSAGE",
});
